import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { apiFetch } from '../lib/api';
import { useSession } from '../lib/session';
import { CapMark } from '../components/Brand';
import { Badge, Banner, Button, Card, Empty, Field, PageHeader, Spinner } from '../components/ui';

interface Store {
  id: string;
  name: string;
  address: string | null;
  isActive?: boolean;
  canManage?: boolean;
  productCount?: number;
  orderCount?: number;
}

export function Stores() {
  const session = useSession();
  const stores = useQuery({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/api/stores'),
  });
  const staff = session.data?.capabilities.manageAssignedStores === true;

  if (stores.isPending) return <Spinner label="Finding stores" />;

  const all = stores.data ?? [];
  const shoppable = all.filter((store) => store.isActive !== false);

  return (
    <div className="space-y-10">
      <section>
        <PageHeader
          title="Choose a store"
          subtitle="You can shop at any of them. Prices and deals are set per store."
        />

        {shoppable.length === 0 ? (
          <div className="mt-7">
            <Empty
              title="No stores are open"
              body={
                staff
                  ? 'Open one below and stock it, and it will appear here.'
                  : 'Nothing is trading right now. Check back shortly.'
              }
            />
          </div>
        ) : (
          <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {shoppable.map((store, index) => (
              <Link
                key={store.id}
                to={`/store/${store.id}`}
                className="tag tag-hover rise group flex flex-col p-5"
                style={{ animationDelay: `${index * 45}ms` }}
              >
                <div className="flex items-start justify-between gap-3">
                  <CapMark className="h-5 w-5 shrink-0 text-ochre-500" />
                  {store.canManage && <Badge tone="good">Yours</Badge>}
                </div>

                <h2 className="mt-3 text-xl leading-tight text-ink-950">{store.name}</h2>
                <p className="mt-1.5 text-[14px] text-ink-500">
                  {store.address ?? 'Address not set'}
                </p>

                <span className="sign mt-4 text-[10px] text-leaf-700 transition-transform group-hover:translate-x-0.5">
                  Shop here →
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/*
        Store administration lives on the page that lists stores, not buried in
        Manage. "Where do I add a store?" has exactly one plausible answer, and
        this is it.
      */}
      {staff && <StoreAdmin stores={all} />}
    </div>
  );
}

function StoreAdmin({ stores }: { stores: Store[] }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mine = stores.filter((store) => store.canManage);

  const refresh = async () => {
    setError(null);
    await queryClient.invalidateQueries({ queryKey: ['stores'] });
  };

  const create = useMutation({
    mutationFn: (body: { name: string; address?: string }) =>
      apiFetch<Store>('/api/stores', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: async () => {
      setName('');
      setAddress('');
      await refresh();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <section className="border-t hairline pt-8">
      <PageHeader
        title="Your stores"
        subtitle="Open a new branch, rename one, close it to customers, or remove it entirely."
      />

      {error && (
        <div className="mt-5">
          <Banner tone="error">{error}</Banner>
        </div>
      )}

      <Card className="mt-6">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!name.trim()) return;
            create.mutate({
              name: name.trim(),
              ...(address.trim() ? { address: address.trim() } : {}),
            });
          }}
        >
          <div className="min-w-48 flex-1">
            <Field
              label="Open a new store"
              placeholder="e.g. Argent Street"
              required
              minLength={2}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="min-w-48 flex-1">
            <Field
              label="Address"
              placeholder="Optional"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
            />
          </div>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? 'Opening…' : 'Open store'}
          </Button>
        </form>
      </Card>

      {mine.length === 0 ? (
        <div className="mt-5">
          <Empty
            title="No stores assigned to you"
            body="Open one above, or ask the owner to assign you to an existing store."
          />
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          {mine.map((store) => (
            <StoreRow key={store.id} store={store} onDone={refresh} onError={setError} />
          ))}
        </div>
      )}
    </section>
  );
}

function StoreRow({
  store,
  onDone,
  onError,
}: {
  store: Store;
  onDone: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(store.name);
  const [address, setAddress] = useState(store.address ?? '');
  const [confirming, setConfirming] = useState(false);
  const [typedName, setTypedName] = useState('');

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<Store>(`/api/stores/${store.id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: async () => {
      setEditing(false);
      await onDone();
    },
    onError: (err: Error) => onError(err.message),
  });

  const remove = useMutation({
    mutationFn: (confirm?: string) =>
      apiFetch(`/api/stores/${store.id}`, {
        method: 'DELETE',
        ...(confirm === undefined ? {} : { body: JSON.stringify({ confirm }) }),
      }),
    onSuccess: async () => {
      setConfirming(false);
      setTypedName('');
      await onDone();
    },
    onError: (err: Error) => onError(err.message),
  });

  /*
   * A store that has traded can still be deleted, but doing so destroys its
   * sales, its receipts and every statistic drawn from them. So the name has to
   * be typed by hand: short enough to type, specific enough that typing it
   * cannot be muscle memory. The server demands the same string again.
   */
  const hasSales = (store.orderCount ?? 0) > 0;
  const nameMatches = typedName.trim() === store.name.trim();

  return (
    <div className="tag p-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <CapMark className="h-4 w-4 shrink-0 text-ochre-500" />
        <span className="text-[15px] text-ink-950">{store.name}</span>
        {store.isActive === false && <Badge tone="danger">Closed</Badge>}

        <span className="tabular text-[13px] text-ink-500">
          {store.productCount ?? 0} stocked · {store.orderCount ?? 0} sales
        </span>

        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="quiet" onClick={() => setEditing((open) => !open)}>
            {editing ? 'Cancel' : 'Edit'}
          </Button>
          <Button
            variant="quiet"
            disabled={update.isPending}
            onClick={() => update.mutate({ isActive: store.isActive === false })}
          >
            {store.isActive === false ? 'Reopen' : 'Close'}
          </Button>

          {confirming ? (
            <>
              <Button
                variant="danger"
                disabled={remove.isPending || (hasSales && !nameMatches)}
                onClick={() => remove.mutate(hasSales ? typedName.trim() : undefined)}
              >
                {remove.isPending ? 'Deleting…' : `Delete ${store.name}`}
              </Button>
              <Button
                variant="quiet"
                onClick={() => {
                  setConfirming(false);
                  setTypedName('');
                }}
              >
                Keep it
              </Button>
            </>
          ) : (
            <Button variant="quiet" onClick={() => setConfirming(true)}>
              Delete
            </Button>
          )}
        </div>
      </div>

      {confirming && (
        <div className="mt-3">
          <p className="text-[13px] text-tomato-700">
            {hasSales ? (
              <>
                This permanently destroys {store.orderCount} sale
                {store.orderCount === 1 ? '' : 's'} and every receipt and statistic that came from
                them, along with everything {store.name} stocks. It cannot be undone.
              </>
            ) : (
              <>
                This removes {store.name}, everything it stocks and its price deals. It cannot be
                undone.
              </>
            )}
          </p>
          {hasSales && (
            <div className="mt-2 max-w-xs">
              <Field
                label={`Type "${store.name}" to confirm`}
                autoComplete="off"
                spellCheck={false}
                value={typedName}
                onChange={(event) => setTypedName(event.target.value)}
              />
            </div>
          )}
        </div>
      )}

      {hasSales && !confirming && (
        <p className="mt-2 text-[12.5px] text-ink-500">
          Has {store.orderCount} sale{store.orderCount === 1 ? '' : 's'} on record. Closing hides it
          from customers and keeps the books; deleting destroys them.
        </p>
      )}

      {editing && (
        <form
          className="mt-4 flex flex-wrap items-end gap-3 border-t hairline pt-4"
          onSubmit={(event) => {
            event.preventDefault();
            update.mutate({ name: name.trim(), address: address.trim() || null });
          }}
        >
          <div className="min-w-44 flex-1">
            <Field
              label="Name"
              required
              minLength={2}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="min-w-44 flex-1">
            <Field
              label="Address"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
            />
          </div>
          <Button type="submit" variant="primary" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </form>
      )}
    </div>
  );
}
